import { useTheme } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { MonthlyTimesheet, SelectItem, User, UserWithTsPeriodData } from '../../../types';

import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { AwerageWorkCell } from '../components/AwerageWorkCell';
import { getMonthLabelShort } from '../../../utils/formatters';

const columnHelper = createColumnHelper<UserWithTsPeriodData>();

export const useAccountantTsOverviewColumns = (users: User[]) => {
  const { colors } = useTheme();

  const userWithLargestTimesheet = users.reduce((prevUser, currentUser) => {
    if (
      (currentUser?.monthlyTimesheets || []).length > (prevUser?.monthlyTimesheets || []).length
    ) {
      return currentUser;
    } else {
      return prevUser;
    }
  }, users[0]);

  const columns = useMemo(() => {
    const fixedColumns = [
      columnHelper.accessor('name', {
        cell: (info) => (
          <AvatarCell
            avatarBoxStyle={{
              background: colors.surfaceIndicator.needsReview,
            }}
            avatarLetterStyle={{
              color: colors.text.main,
            }}
            name={info.getValue()}
          />
        ),
        header: () => <TableHeaderCell text='Submitter' />,
        sortingFn: (rowA, rowB) => {
          const valueA = rowA.original?.name || '';
          const valueB = rowB.original?.name || '';
          return valueA.localeCompare(valueB);
        },
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((user) => user.id === row.original.id);
        },
        meta: {
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
    ];

    const periodCols = (userWithLargestTimesheet?.monthlyTimesheets || [])
      .map((ts) => `${ts.year}-${ts.month}`)
      .flatMap((period) => {
        return columnHelper.accessor(period, {
          cell: (info) => {
            return <AwerageWorkCell timesheet={info.getValue() as MonthlyTimesheet} />;
          },
          header: () => {
            const [year, month] = period.split('-');
            const monthLabel = getMonthLabelShort(Number(month));
            return <TableHeaderCell text={`${monthLabel} ${year}`} />;
          },
          meta: {
            width: '140px',
            maxWidth: '140px',
            minWidth: '140px',
            alignHeader: 'right',
            thStyles: { borderLeft: 'none' },
            tdStyles: { borderLeft: 'none', paddingRight: '16px' },
          },
          enableSorting: true,
        });
      });
    return [...fixedColumns, ...periodCols];
  }, [
    colors.surfaceIndicator.needsReview,
    colors.text.main,
    userWithLargestTimesheet?.monthlyTimesheets,
  ]);

  return columns;
};
